import { api } from "@/lib/api";

interface ApiResponse<T> {
  data: T;
  message?: string;
  status?: string;
}

export type Bucket = "day" | "month";
export type DashboardGranularity = "day" | "week" | "month" | "year";

export interface RevenueBreakdownResponse {
  from: string;
  to: string;
  bucket: Bucket;
  totals: { opd: number; lab: number; pharma: number; additionalServices: number; ipd: number; dc: number };
  series: Array<{
    bucket: string;
    opd: number;
    lab: number;
    pharma: number;
    additionalServices: number;
    ipd: number;
    dc: number;
  }>;
}

export interface NewPatientsResponse {
  from: string;
  to: string;
  bucket: Bucket;
  total: number;
  series: Array<{ bucket: string; count: number }>;
}

export interface PatientSourcesResponse {
  from: string;
  to: string;
  bySource: Array<{ source: string; count: number }>;
  referrals: Array<{ name: string; count: number }>;
}

export interface PatientRevenueRow {
  patientId: string;
  patientName: string;
  uhid?: string;
  phone: string;
  hospitalName?: string;
  opd: number;
  lab: number;
  pharma: number;
  additionalServices?: number;
  ipd: number;
  dc: number;
  icu: number;
  total: number;
}

export interface PatientRevenueAllResponse {
  from: string;
  to: string;
  patients: PatientRevenueRow[];
}

export interface PlasticSurgeryRevenueRow {
  patientId: string;
  patientName: string;
  uhid?: string | null;
  phone?: string | null;
  hospitalName?: string | null;
  doctorNames?: string | null;
  specialisations?: string | null;
  opd: number;
  lab: number;
  pharma: number;
  ipd: number;
  total: number;
}

export interface PlasticSurgeryRevenueReport {
  from: string;
  to: string;
  totals: { opd: number; lab: number; pharma: number; ipd: number; total: number };
  plasticDoctorIds: string[];
  plasticDoctorNames: string[];
  rows: PlasticSurgeryRevenueRow[];
}

export interface PlasticSurgeryProfitabilityRow {
  patientId: string;
  patientName: string;
  uhid?: string | null;
  phone?: string | null;
  hospitalName?: string | null;
  ipdNumbers?: string | null;
  admittingDoctors?: string | null;
  opd: number;
  lab: number;
  pharma: number;
  ipd: number;
  totalRevenue: number;
  totalExpense: number;
  profit: number;
}

export interface PlasticSurgeryProfitabilityReport {
  from: string;
  to: string;
  totals: {
    opd: number;
    lab: number;
    pharma: number;
    ipd: number;
    totalRevenue: number;
    totalExpense: number;
    profit: number;
  };
  plasticDoctorIds: string[];
  plasticDoctorNames: string[];
  rows: PlasticSurgeryProfitabilityRow[];
}

export type BillingReportType =
  | "All"
  | "OPD"
  | "IPD"
  | "DC"
  | "ICU"
  | "Lab"
  | "Pharma"
  | "AdditionalServices";

export interface PatientBillingDetailResponse {
  from: string;
  to: string;
  type: BillingReportType;
  patients: PatientRevenueRow[];
}

export interface PatientBillingDetailItemRow {
  patientId: string;
  patientName: string;
  uhid?: string;
  phone: string;
  hospitalName?: string;
  category: string;
  itemType: string;
  itemDescription: string;
  chargeCode?: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  serviceDate: string;
}

export interface PatientBillingDetailItemsResponse {
  from: string;
  to: string;
  type: BillingReportType;
  items: PatientBillingDetailItemRow[];
}

export type PatientMasterScope = 'all' | 'registered' | 'active';

export interface PatientMasterRow {
  patientId: string;
  uhid: string | null;
  patientName: string;
  phone: string;
  email: string | null;
  gender: string;
  dob: string | null;
  ageYears: number | null;
  address: string | null;
  bloodGroup: string | null;
  maritalStatus: string | null;
  registrationSource: string;
  registrationSourceDetails: string | null;
  referrerName: string | null;
  registrationDate: string;
  status: string;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  allergy: string | null;
  preExistingCondition: string | null;
  chronicDisease: string | null;
  familyDetails: string | null;
  hospitalName: string | null;
  opdVisits: number;
  ipdAdmissions: number;
  dayCareCases: number;
  totalVisits: number;
  opdBilling: number;
  labBilling: number;
  pharmaBilling: number;
  ipdBilling: number;
  dayCareBilling: number;
  icuBilling: number;
  totalBilling: number;
  firstAppLoginAt: string | null;
  createdByStaff: string | null;
}

export interface PatientMasterReportResponse {
  scope: PatientMasterScope;
  from: string | null;
  to: string | null;
  totalPatients: number;
  truncated?: boolean;
  summary: {
    totalPatients: number;
    totalVisits: number;
    totalBilling: number;
  };
  rows: PatientMasterRow[];
}

export type SalesChannelMetrics = { count: number; revenue: number };

export type SalesAttributionRow = {
  name: string;
  opd: SalesChannelMetrics;
  ipd: SalesChannelMetrics;
  lab: SalesChannelMetrics;
  pharma: SalesChannelMetrics;
  daycare: SalesChannelMetrics;
  totalRevenue: number;
};

export type CrmMissedIpdConversionRow = {
  admissionId: string;
  ipdNumber: string | null;
  patientName: string;
  phone: string | null;
  admissionDate: string;
  dischargeDate: string;
  crmLeadId: string;
  crmLeadCreatedAt: string;
  crmSalesPersonName: string;
  leadSource: string | null;
  campaignName: string | null;
  formSalesPersonName: string | null;
  partnerLeadName: string | null;
  referrerName: string | null;
  ipdRevenue: number;
};

export type SalesAttributionReportResponse = {
  from: string;
  to: string;
  hospitalId: string | null;
  periodTotals: {
    opd: number;
    ipd: number;
    lab: number;
    pharma: number;
    daycare: number;
    total: number;
  };
  crmSales: SalesAttributionRow[];
  formSales: SalesAttributionRow[];
  partnerLead: SalesAttributionRow[];
  referrer: SalesAttributionRow[];
  crmMissedIpdConversions: CrmMissedIpdConversionRow[];
};

export type SalesAttributionDimension = "crm" | "form_sales" | "partner_lead" | "referrer";

export type SalesAttributionCaseRow = {
  channel: "OPD_BILL" | "IPD" | "DAYCARE";
  caseId: string;
  caseNumber: string | null;
  patientName: string;
  phone: string | null;
  caseDate: string;
  opdRevenue: number;
  labRevenue: number;
  pharmaRevenue: number;
  ipdRevenue: number;
  daycareRevenue: number;
  totalRevenue: number;
};

export type SalesAttributionCasesResponse = {
  dimension: SalesAttributionDimension;
  name: string;
  from: string;
  to: string;
  hospitalId: string | null;
  cases: SalesAttributionCaseRow[];
  totals: {
    opdRevenue: number;
    labRevenue: number;
    pharmaRevenue: number;
    ipdRevenue: number;
    daycareRevenue: number;
    totalRevenue: number;
    caseCount: number;
  };
};

type ReportParams = {
  from: string;
  to: string;
  hospitalId?: string | null;
  /** YYYY-MM — filters expense rows by the service month entered on the expense form */
  serviceMonth?: string;
  /** When true, include line-level expense rows (patient + central) */
  includeLines?: boolean;
};

/** Preserve hospitalId=all for chain consolidated reports (do not coerce to undefined). */
function reportQueryParams(
  params: Record<string, unknown> & { hospitalId?: string | null }
): Record<string, unknown> {
  const { hospitalId, ...rest } = params;
  if (hospitalId == null || hospitalId === '') {
    return rest;
  }
  return { ...rest, hospitalId: String(hospitalId) };
}

export type PaymentMethodKey = "CASH" | "UPI" | "CARD" | "BANK_TRANSFER" | "INSURANCE" | "CHEQUE";
export type ServiceTypeKey =
  | "OPD"
  | "IPD"
  | "Pharma"
  | "Lab"
  | "DayCare"
  | "AdditionalServices"
  | "Emergency";

export interface PaymentByServiceTypeResponse {
  from: string;
  to: string;
  table: Record<ServiceTypeKey, Record<PaymentMethodKey, number>>;
}

export interface ExpenseReportResponse {
  from: string;
  to: string;
  serviceMonth?: string | null;
  totals: {
    ipdPatient: number;
    ipdReception?: number;
    ipdAdmin?: number;
    central: number;
    centralReception?: number;
    centralAdmin?: number;
    total: number;
  };
  series: Array<{
    bucket: string;
    ipdPatient: number;
    ipdReception?: number;
    ipdAdmin?: number;
    central: number;
    centralReception?: number;
    centralAdmin?: number;
    total: number;
  }>;
  lines?: Array<{
    expenseType: 'PATIENT' | 'CENTRAL';
    expenseId?: string;
    expenseNumber?: string | null;
    expenseDate: string;
    category: string;
    description: string | null;
    vendorName: string | null;
    amount: number;
    serviceMonth: string | null;
    createdByName?: string | null;
    workflowStatus?: string;
    proofUrl?: string | null;
    proofFileName?: string | null;
    proofs?: Array<{ id: string; url: string; fileName: string }>;
  }>;
}

export interface MonthlyExpensesByServiceMonthResponse {
  serviceMonth: string;
  totals: {
    ipdPatient: number;
    ipdReception: number;
    ipdAdmin: number;
    central: number;
    centralReception: number;
    centralAdmin: number;
    total: number;
  };
  byCategory: Array<{
    expenseType: 'PATIENT' | 'CENTRAL';
    category: string;
    amount: number;
    count: number;
  }>;
  lines: NonNullable<ExpenseReportResponse['lines']>;
}

type ServiceMonthReportParams = {
  serviceMonth: string;
  hospitalId?: string | null;
};

export interface PaymentLinesReportSummary {
  totalPatientDays: number;
  totalOpdCount: number;
  totalOpdRevenue: number;
  totalLabRevenue: number;
  totalPharmaRevenue: number;
  totalRevenue: number;
}

export interface PaymentLineReportRow {
  uhidNo: string;
  patientName: string;
  mobile?: string;
  doctorName: string;
  paymentMode: string;
  amount: number;
  opdAmount?: number;
  labAmount?: number;
  pharmaAmount?: number;
  totalAmount?: number;
  actualOpdAmount?: number;
  actualLabAmount?: number;
  actualPharmaAmount?: number;
  actualBillNet?: number;
  serviceDay?: string;
  date: string;
  opdSource: string;
  opdPayoutRecorded?: boolean;
  opdPayoutAmount?: number | null;
}

export interface PaymentLinesReportResponse {
  from: string;
  to: string;
  truncated?: boolean;
  summary?: PaymentLinesReportSummary;
  rows: PaymentLineReportRow[];
}

export interface PharmaRevenueLineRow {
  source: "BILLED" | "IPD_PHARMA_COLLECTION";
  date: string;
  paymentMode: string | null;
  amount: number;
  billNo: string;
  billDate?: string | null;
  billStatus?: string | null;
  patientName: string;
  uhid?: string | null;
  phone?: string | null;
  medicineName?: string;
  genericName?: string;
  brandName?: string;
  strength?: string;
  form?: string;
  batchNo?: string;
  quantity?: number;
  unitPrice?: number;
  discountAmount?: number;
  hsnCode?: string;
  gstRate?: number | "";
  gstAmount?: number;
}

export interface PharmaRevenueLinesResponse {
  from: string;
  to: string;
  truncated?: boolean;
  total: number;
  rows: PharmaRevenueLineRow[];
}

export interface PharmaConsumptionByDrugRow {
  drugId: string;
  medicineName: string;
  genericName: string | null;
  brandName: string | null;
  strength: string | null;
  form: string | null;
  unit: string | null;
  totalQty: number;
  costValue: number;
  mrpValue: number;
  opdQty: number;
  ipdQty: number;
  otQty: number;
  opdCostValue: number;
  ipdCostValue: number;
  otCostValue: number;
  opdMrpValue: number;
  ipdMrpValue: number;
  otMrpValue: number;
}

export interface PharmaConsumptionByDrugTotals {
  totalQty: number;
  costValue: number;
  mrpValue: number;
  opdQty: number;
  ipdQty: number;
  otQty: number;
}

export interface PharmaConsumptionByDrugResponse {
  from: string;
  to: string;
  totals: PharmaConsumptionByDrugTotals;
  rows: PharmaConsumptionByDrugRow[];
}

export interface LabBillingLineRow {
  source: "OPD_BILL" | "IPD_BILL" | "DAY_CARE_BILL";
  date: string;
  billNo: string;
  billDate?: string | null;
  billStatus: string;
  patientName: string;
  uhid?: string | null;
  phone?: string | null;
  testCode: string;
  testName: string;
  sampleType: string;
  processingType: string;
  testStatus: string;
  externalLabName: string;
  recommendedDoctor: string;
  quantity: number;
  unitPrice: number;
  discountAmount: number;
  gstRate?: number | "";
  gstAmount: number;
  amount: number;
}

export interface LabBillingLinesResponse {
  from: string;
  to: string;
  truncated?: boolean;
  total: number;
  rows: LabBillingLineRow[];
}

export interface GstFilingLineRow {
  id: string;
  date: string;
  module: string;
  billNo: string;
  billDate: string | null;
  billStatus: string;
  hospitalName: string;
  hospitalGstin: string;
  patientName: string;
  uhid: string | null;
  phone: string | null;
  description: string;
  itemType: string;
  hsnSac: string;
  quantity: number;
  taxableValue: number;
  gstRate: number;
  cgst: number;
  sgst: number;
  igst: number;
  gstAmount: number;
  invoiceValue: number;
  gstInclusive: boolean;
}

export interface GstFilingReportResponse {
  from: string;
  to: string;
  truncated?: boolean;
  summary: {
    lineCount: number;
    totalTaxableValue: number;
    totalCgst: number;
    totalSgst: number;
    totalIgst: number;
    totalGst: number;
    totalInvoiceValue: number;
    byRate: Array<{ gstRate: number; lineCount: number; taxableValue: number; gstAmount: number }>;
    byModule: Array<{ module: string; lineCount: number; taxableValue: number; gstAmount: number }>;
  };
  rows: GstFilingLineRow[];
}

export interface AdditionalServicesBillingLineRow {
  date: string;
  billDate?: string;
  billId: string;
  billNo: string;
  billStatus: string;
  patientName: string;
  doctorName?: string | null;
  uhid?: string | null;
  phone?: string | null;
  serviceName: string;
  quantity: number;
  unitPrice: number;
  discountAmount: number;
  amount: number;
  paidAmount: number;
  dueAmount: number;
  paymentModes: string;
}

export interface AdditionalServicesBillingResponse {
  from: string;
  to: string;
  truncated?: boolean;
  total: number;
  /** Cash collected in period (payment date) — use as Additional Services revenue for EMI packages. */
  totalCollected?: number;
  billCount: number;
  rows: AdditionalServicesBillingLineRow[];
}

export interface DashboardAnalyticsResponse {
  from: string;
  to: string;
  granularity: DashboardGranularity;
  revenue: {
    totals: {
      opd: number;
      lab: number;
      pharma: number;
      additionalServices: number;
      ipd: number;
      dc: number;
      total: number;
    };
    series: Array<{
      bucket: string;
      opd: number;
      lab: number;
      pharma: number;
      additionalServices: number;
      ipd: number;
      dc: number;
      total: number;
    }>;
  };
  expenses: {
    totals: { ipdPatient: number; central: number; total: number };
    series: Array<{ bucket: string; ipdPatient: number; central: number }>;
  };
  opdCount: {
    total: number;
    series: Array<{ bucket: string; count: number }>;
  };
  patientsBilled: {
    totals: {
      unique: number;
      repeat: number;
      uniquePlastic?: number;
      repeatPlastic?: number;
    };
    series: Array<{
      bucket: string;
      unique: number;
      repeat: number;
      uniquePlastic?: number;
      repeatPlastic?: number;
    }>;
  };
  /** Customer Voice summary — cumulative IPD NPS + OPD doctor feedback (not date-filtered). */
  customerVoice?: {
    npsAverage: number | null;
    npsCount: number;
    doctorFeedbackAverage: number | null;
    doctorFeedbackCount: number;
  };
  /** Live Google Business Profile rating (not date-filtered). */
  gmbRating?: {
    rating: number | null;
    reviewCount: number;
    locationCount: number;
    name: string | null;
    configured?: boolean;
    hint?: string | null;
  };
  surgeryRevenue: Array<{ surgeryName: string; revenue: number; admissionCount: number }>;
  expenseMom: {
    currentMonth: string;
    previousMonth: string;
    currentLabel?: string;
    previousLabel?: string;
    rows: Array<{
      header: string;
      source: string;
      category: string;
      subCategory: string | null;
      currentAmount: number;
      previousAmount: number;
      changeAmount: number;
      changePercent: number;
    }>;
  };
  expensePareto: Array<{
    header: string;
    amount: number;
    sharePercent: number;
    cumulativePercent: number;
  }>;
  revenueByLeadSource: Array<{
    source: string;
    revenue: number;
    sharePercent: number;
  }>;
  revenueByLeadSourceMom: {
    months: string[];
    monthLabels: string[];
    series: Array<{
      source: string;
      revenues: number[];
    }>;
  };
}

export type MonthlyPerformanceAudience = "hospital" | "chain" | "platform";

export type PerformanceInsight = {
  severity: "critical" | "attention" | "positive" | "info";
  category: string;
  title: string;
  description: string;
  recommendedAction: string | null;
};

export type MonthlyPnlSnapshot = {
  periodLabel: string;
  ipdCount: number;
  grossSales: number;
  netSales: number;
  ebitda: number;
  ebitdaMarginPercent: number;
};

export type MonthlyBusinessPerformanceResponse = {
  month: string;
  monthLabel: string;
  comparisonMonthLabel: string;
  generatedAt: string;
  audienceLevel: MonthlyPerformanceAudience;
  scope: {
    hospitalId: string | null;
    hospitalIds: string[] | null;
    hospitalName: string | null;
    isConsolidated: boolean;
  };
  executiveSummary: string[];
  insights: PerformanceInsight[];
  commandCentre: {
    kpis: import("@/api/intelligence").CommandCentreKpi[];
    managementAttention: import("@/api/intelligence").ManagementAttentionResponse;
    domainSummaries: import("@/api/intelligence").DomainSummary[];
    hospitalComparison: import("@/api/intelligence").CommandCentreResponse["hospitalComparison"];
    funnel: import("@/api/intelligence").CommandCentreResponse["funnel"];
    findings: import("@/api/intelligence").CommandCentreResponse["findings"];
    benchmarkSummary: import("@/api/intelligence").CommandCentreResponse["benchmarkSummary"];
  };
  businessReports: DashboardAnalyticsResponse;
  pnl: MonthlyPnlSnapshot | null;
};

export type BillWiseCategory =
  | "All"
  | "OPD"
  | "Lab"
  | "Pharma"
  | "Day Care"
  | "Additional Services"
  | "IPD"
  | "ICU"
  | "IPD Pharma"
  | "Mixed";

export interface BillWiseRow {
  billId: string;
  billNumber: string;
  billCategory: string;
  billDate: string;
  hospitalName?: string | null;
  patientName: string;
  uhid?: string | null;
  phone?: string | null;
  totalAmount: number;
  discountAmount: number;
  netAmount: number;
  paidAmount: number;
  dueAmount: number;
  status: string;
  paymentModes: string;
  opdAmount: number;
  labAmount: number;
  pharmaAmount: number;
  additionalServicesAmount?: number;
  ipdNumber?: string | null;
  admissionId?: string | null;
}

export interface BillWiseReportResponse {
  from: string;
  to: string;
  category: BillWiseCategory;
  totals: { count: number; netAmount: number; paidAmount: number };
  bills: BillWiseRow[];
}

export type HospitalPnlLine = {
  id: string;
  label: string;
  value: number;
  format: "number" | "currency" | "percent";
  section: string;
  formula?: string;
  note?: string;
  isOverridden?: boolean;
  computedValue?: number;
  isEstimated?: boolean;
};

export type PnlAestheticProceduresBreakdownResponse = {
  periodLabel: string;
  truncated: boolean;
  metrics: {
    patientCount: number;
    teamLeadId: string | null;
    teamLeadName: string | null;
    teamDoctorCount: number;
    minBillAmount: number;
  };
  items: Array<{
    patientId: string;
    patientName: string;
    uhid: string | null;
    phone: string | null;
    billId: string;
    billNo: string;
    billDate: string;
    billTotal: number;
    serviceName: string;
    lineAmount: number;
    doctorName: string | null;
  }>;
};

export type PnlIpdDirectCostBreakdownResponse = {
  lineId: string;
  lineLabel: string;
  periodLabel: string;
  sourceNote: string;
  total: number;
  showPartnerSummary: boolean;
  partnerSummary: Array<{
    partnerName: string;
    amount: number;
    admissionCount: number;
  }>;
  items: Array<{
    admissionId: string;
    ipdNumber: string;
    patientName: string;
    uhid: string | null;
    financingPartnerName: string | null;
    amount: number;
  }>;
};

export type PnlExpenseBreakdownResponse = {
  lineId: string;
  label: string;
  periodLabel: string;
  consolidated: boolean;
  categorySummary: Array<{ category: string; label: string; amount: number; count: number }>;
  items: Array<{
    id: string;
    category: string;
    categoryLabel: string;
    description: string | null;
    vendorName: string | null;
    expenseEmployeeName: string | null;
    amount: number;
    serviceMonth: string | null;
    expenseDate: string;
    hospitalUnit: string;
    /** When false, omitted from P&L and other financial report totals. */
    includedInReports?: boolean;
  }>;
  total: number;
  sourceNote?: string;
};

export type CashFlowBreakdownResponse = {
  lineId: string;
  label: string;
  periodLabel: string;
  consolidated: boolean;
  categorySummary: Array<{ category: string; label: string; amount: number; count: number }>;
  items: Array<{
    id: string;
    source?: string;
    category: string;
    categoryLabel: string;
    description: string | null;
    vendorName: string | null;
    expenseEmployeeName: string | null;
    amount: number;
    paidAt?: string;
    /** Central expenses only — when false, omitted from cash-flow report totals. */
    includedInReports?: boolean;
  }>;
  total: number;
  sourceNote?: string;
};

export type HospitalPnlResponse = {
  view?: "SINGLE";
  consolidated?: boolean;
  period: {
    mode: "MTD" | "YTD";
    from: string;
    to: string;
    label: string;
    periodKey: string;
    serviceMonths: string[];
  };
  priorPeriod: {
    mode: "MTD" | "YTD";
    from: string;
    to: string;
    label: string;
    periodKey: string;
  };
  lines: HospitalPnlLine[];
  breakdown: {
    opd: number;
    lab: number;
    pharma: number;
    additionalServices: number;
    dayCare: number;
    a13: number;
    nonSalariedOpd: number;
    ipdDoctorShare: number;
  };
};

export type HospitalPnlMomResponse = {
  view: "MOM";
  fyStartYear: number;
  label: string;
  months: Array<{
    month: string;
    label: string;
    lines: HospitalPnlLine[];
  }>;
  totalLines: HospitalPnlLine[];
};

export type HospitalPnlApiResponse = HospitalPnlResponse | HospitalPnlMomResponse;

export type HospitalCashFlowLine = {
  id: string;
  label: string;
  value: number;
  format: "number" | "currency";
  section: string;
  formula?: string;
  note?: string;
  isOverridden?: boolean;
  computedValue?: number;
};

export type HospitalCashFlowResponse = {
  view?: "SINGLE";
  period: {
    mode: "MTD" | "YTD";
    from: string;
    to: string;
    label: string;
    periodKey: string;
    serviceMonths: string[];
  };
  lines: HospitalCashFlowLine[];
  closing: { cash: number; bank: number; fdr: number; inventory: number };
};

export type HospitalCashFlowMomResponse = {
  view: "MOM";
  fyStartYear: number;
  label: string;
  months: Array<{
    month: string;
    label: string;
    lines: HospitalCashFlowLine[];
  }>;
};

export type HospitalCashFlowApiResponse = HospitalCashFlowResponse | HospitalCashFlowMomResponse;

export const businessReportsApi = {
  dashboardAnalytics: (params: ReportParams & { granularity?: DashboardGranularity }) =>
    api
      .get<ApiResponse<DashboardAnalyticsResponse>>("/api/hospital-admin/reports/dashboard-analytics", {
        params: reportQueryParams(params),
      })
      .then((r) => r.data.data),

  monthlyBusinessPerformance: (params: { month: string; hospitalId?: string }) =>
    api
      .get<ApiResponse<MonthlyBusinessPerformanceResponse>>("/api/hospital-admin/reports/monthly-performance", {
        params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      })
      .then((r) => r.data.data),

  revenueBreakdown: (params: ReportParams & { bucket?: Bucket }) =>
    api
      .get<ApiResponse<RevenueBreakdownResponse>>("/api/hospital-admin/reports/revenue-breakdown", {
        params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      })
      .then((r) => r.data.data),

  revenueBreakdownExport: (params: ReportParams & { bucket?: Bucket }) =>
    api.get<Blob>("/api/hospital-admin/reports/revenue-breakdown/export", {
      params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      responseType: "blob",
    }),

  paymentByServiceType: (params: ReportParams) =>
    api
      .get<ApiResponse<PaymentByServiceTypeResponse>>("/api/hospital-admin/reports/payment-by-service-type", {
        params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      })
      .then((r) => r.data.data),

  newPatients: (params: ReportParams & { bucket?: Bucket }) =>
    api
      .get<ApiResponse<NewPatientsResponse>>("/api/hospital-admin/reports/patients/new", {
        params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      })
      .then((r) => r.data.data),

  patientSources: (params: ReportParams) =>
    api
      .get<ApiResponse<PatientSourcesResponse>>("/api/hospital-admin/reports/patients/sources", {
        params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      })
      .then((r) => r.data.data),

  patientRevenueAll: (params: ReportParams) =>
    api
      .get<ApiResponse<PatientRevenueAllResponse>>("/api/hospital-admin/reports/patients/revenue", {
        params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      })
      .then((r) => r.data.data),

  patientRevenueAllExport: (params: ReportParams) =>
    api.get<Blob>("/api/hospital-admin/reports/patients/revenue/export", {
      params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      responseType: "blob",
    }),

  patientBillingDetail: (params: ReportParams & { type?: BillingReportType }) =>
    api
      .get<ApiResponse<PatientBillingDetailResponse>>("/api/hospital-admin/reports/patients/billing-detail", {
        params: {
          from: params.from,
          to: params.to,
          type: params.type ?? "All",
          ...(params.hospitalId != null && params.hospitalId && { hospitalId: params.hospitalId }),
        },
      })
      .then((r) => r.data.data),

  patientBillingDetailExport: (params: ReportParams & { type?: BillingReportType }) =>
    api.get<Blob>("/api/hospital-admin/reports/patients/billing-detail/export", {
      params: {
        from: params.from,
        to: params.to,
        type: params.type ?? "All",
        ...(params.hospitalId != null && params.hospitalId && { hospitalId: params.hospitalId }),
      },
      responseType: "blob",
    }),

  patientBillingDetailItems: (params: ReportParams & { type?: BillingReportType }) =>
    api
      .get<ApiResponse<PatientBillingDetailItemsResponse>>("/api/hospital-admin/reports/patients/billing-detail/items", {
        params: {
          from: params.from,
          to: params.to,
          type: params.type ?? "All",
          ...(params.hospitalId != null && params.hospitalId && { hospitalId: params.hospitalId }),
        },
      })
      .then((r) => r.data.data),

  patientBillingDetailItemsExport: (params: ReportParams & { type?: BillingReportType }) =>
    api.get<Blob>("/api/hospital-admin/reports/patients/billing-detail/items/export", {
      params: {
        from: params.from,
        to: params.to,
        type: params.type ?? "All",
        ...(params.hospitalId != null && params.hospitalId && { hospitalId: params.hospitalId }),
      },
      responseType: "blob",
    }),

  ipdDischargedProfitability: (params: ReportParams) =>
    api
      .get<ApiResponse<{ from: string; to: string; rows: any[] }>>(
        "/api/hospital-admin/reports/ipd/discharged-profitability",
        {
          params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
        }
      )
      .then((r) => r.data.data),

  ipdDischargedProfitabilityExport: (params: ReportParams) =>
    api.get<Blob>("/api/hospital-admin/reports/ipd/discharged-profitability/export", {
      params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      responseType: "blob",
    }),

  /** Admin: set IPD admission plastic surgery flag (drives Plastic Surgery reports). */
  updateIpdPlasticSurgeryFlag: (admissionId: string, isPlasticSurgery: boolean) =>
    api
      .patch<ApiResponse<{ admissionId: string; isPlasticSurgery: boolean }>>(
        `/api/hospital-admin/reports/ipd/${encodeURIComponent(admissionId)}/plastic-surgery`,
        { isPlasticSurgery }
      )
      .then((r) => r.data.data),

  expensesReport: (params: ReportParams) =>
    api
      .get<ApiResponse<ExpenseReportResponse>>("/api/hospital-admin/reports/expenses", {
        params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      })
      .then((r) => r.data.data),

  expensesReportExport: (params: ReportParams) =>
    api.get<Blob>("/api/hospital-admin/reports/expenses/export", {
      params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      responseType: "blob",
    }),

  monthlyExpensesByServiceMonth: (params: ServiceMonthReportParams) =>
    api
      .get<ApiResponse<MonthlyExpensesByServiceMonthResponse>>(
        "/api/hospital-admin/reports/expenses/by-service-month",
        {
          params:
            params.hospitalId != null
              ? { ...params, hospitalId: params.hospitalId || undefined }
              : params,
        }
      )
      .then((r) => r.data.data),

  monthlyExpensesByServiceMonthExport: (params: ServiceMonthReportParams) =>
    api.get<Blob>("/api/hospital-admin/reports/expenses/by-service-month/export", {
      params:
        params.hospitalId != null
          ? { ...params, hospitalId: params.hospitalId || undefined }
          : params,
      responseType: "blob",
    }),

  opdPaymentLines: (params: ReportParams) =>
    api
      .get<ApiResponse<PaymentLinesReportResponse>>("/api/hospital-admin/reports/opd/payment-lines", {
        params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      })
      .then((r) => r.data.data),

  opdPaymentLinesExport: (params: ReportParams) =>
    api.get<Blob>("/api/hospital-admin/reports/opd/payment-lines/export", {
      params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      responseType: "blob",
    }),

  dayCarePaymentLines: (params: ReportParams) =>
    api
      .get<ApiResponse<PaymentLinesReportResponse>>("/api/hospital-admin/reports/daycare/payment-lines", {
        params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      })
      .then((r) => r.data.data),

  dayCarePaymentLinesExport: (params: ReportParams) =>
    api.get<Blob>("/api/hospital-admin/reports/daycare/payment-lines/export", {
      params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      responseType: "blob",
    }),

  pharmaRevenueLines: (params: ReportParams) =>
    api
      .get<ApiResponse<PharmaRevenueLinesResponse>>("/api/hospital-admin/reports/pharma/revenue-lines", {
        params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      })
      .then((r) => r.data.data),

  pharmaRevenueLinesExport: (params: ReportParams) =>
    api.get<Blob>("/api/hospital-admin/reports/pharma/revenue-lines/export", {
      params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      responseType: "blob",
    }),

  pharmaConsumptionByDrug: (params: ReportParams) =>
    api
      .get<ApiResponse<PharmaConsumptionByDrugResponse>>("/api/hospital-admin/reports/pharma/consumption-by-drug", {
        params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      })
      .then((r) => r.data.data),

  pharmaConsumptionByDrugExport: (params: ReportParams) =>
    api.get<Blob>("/api/hospital-admin/reports/pharma/consumption-by-drug/export", {
      params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      responseType: "blob",
    }),

  labBillingLines: (params: ReportParams) =>
    api
      .get<ApiResponse<LabBillingLinesResponse>>("/api/hospital-admin/reports/lab/billing-lines", {
        params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      })
      .then((r) => r.data.data),

  labBillingLinesExport: (params: ReportParams) =>
    api.get<Blob>("/api/hospital-admin/reports/lab/billing-lines/export", {
      params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      responseType: "blob",
    }),

  gstFilingLines: (params: ReportParams) =>
    api
      .get<ApiResponse<GstFilingReportResponse>>("/api/hospital-admin/reports/gst/filing-lines", {
        params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      })
      .then((r) => r.data.data),

  gstFilingLinesExport: (params: ReportParams) =>
    api.get<Blob>("/api/hospital-admin/reports/gst/filing-lines/export", {
      params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      responseType: "blob",
    }),

  additionalServicesBilling: (params: ReportParams) =>
    api
      .get<ApiResponse<AdditionalServicesBillingResponse>>(
        "/api/hospital-admin/reports/additional-services/billing-lines",
        {
          params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
        }
      )
      .then((r) => r.data.data),

  additionalServicesBillingExport: (params: ReportParams) =>
    api.get<Blob>("/api/hospital-admin/reports/additional-services/billing-lines/export", {
      params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      responseType: "blob",
    }),

  billWiseReport: (params: ReportParams & { category?: BillWiseCategory }) =>
    api
      .get<ApiResponse<BillWiseReportResponse>>("/api/hospital-admin/reports/bills/all", {
        params: {
          from: params.from,
          to: params.to,
          category: params.category ?? "All",
          ...(params.hospitalId != null && params.hospitalId && { hospitalId: params.hospitalId }),
        },
      })
      .then((r) => r.data.data),

  billWiseReportExport: (params: ReportParams & { category?: BillWiseCategory }) =>
    api.get<Blob>("/api/hospital-admin/reports/bills/all/export", {
      params: {
        from: params.from,
        to: params.to,
        category: params.category ?? "All",
        ...(params.hospitalId != null && params.hospitalId && { hospitalId: params.hospitalId }),
      },
      responseType: "blob",
    }),

  plasticSurgeryRevenue: (params: ReportParams) =>
    api
      .get<ApiResponse<PlasticSurgeryRevenueReport>>("/api/hospital-admin/reports/plastic-surgery/revenue", {
        params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      })
      .then((r) => r.data.data),

  plasticSurgeryRevenueExport: (params: ReportParams) =>
    api.get<Blob>("/api/hospital-admin/reports/plastic-surgery/revenue/export", {
      params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      responseType: "blob",
    }),

  plasticSurgeryProfitability: (params: ReportParams) =>
    api
      .get<ApiResponse<PlasticSurgeryProfitabilityReport>>("/api/hospital-admin/reports/plastic-surgery/profitability", {
        params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      })
      .then((r) => r.data.data),

  plasticSurgeryProfitabilityExport: (params: ReportParams) =>
    api.get<Blob>("/api/hospital-admin/reports/plastic-surgery/profitability/export", {
      params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      responseType: "blob",
    }),

  patientMasterReport: (params: { scope: PatientMasterScope; from?: string; to?: string; hospitalId?: string | null }) =>
    api
      .get<ApiResponse<PatientMasterReportResponse>>("/api/hospital-admin/reports/patients/master", {
        params: {
          scope: params.scope,
          ...(params.from ? { from: params.from } : {}),
          ...(params.to ? { to: params.to } : {}),
          ...(params.hospitalId != null && params.hospitalId ? { hospitalId: params.hospitalId } : {}),
        },
      })
      .then((r) => r.data.data),

  patientMasterReportExport: (params: { scope: PatientMasterScope; from?: string; to?: string; hospitalId?: string | null }) =>
    api.get<Blob>("/api/hospital-admin/reports/patients/master/export", {
      params: {
        scope: params.scope,
        ...(params.from ? { from: params.from } : {}),
        ...(params.to ? { to: params.to } : {}),
        ...(params.hospitalId != null && params.hospitalId ? { hospitalId: params.hospitalId } : {}),
      },
      responseType: "blob",
    }),

  salesAttributionReport: (params: ReportParams) =>
    api
      .get<ApiResponse<SalesAttributionReportResponse>>("/api/hospital-admin/reports/sales-attribution", {
        params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      })
      .then((r) => r.data.data),

  salesAttributionReportExport: (params: ReportParams) =>
    api.get<Blob>("/api/hospital-admin/reports/sales-attribution/export", {
      params: params.hospitalId != null ? { ...params, hospitalId: params.hospitalId || undefined } : params,
      responseType: "blob",
    }),

  salesAttributionCases: (params: ReportParams & { dimension: SalesAttributionDimension; name: string }) =>
    api
      .get<ApiResponse<SalesAttributionCasesResponse>>("/api/hospital-admin/reports/sales-attribution/cases", {
        params: {
          from: params.from,
          to: params.to,
          dimension: params.dimension,
          name: params.name,
          ...(params.hospitalId != null && params.hospitalId ? { hospitalId: params.hospitalId } : {}),
        },
      })
      .then((r) => r.data.data),

  hospitalPnl: (params: {
    mode: "MTD" | "YTD" | "MOM";
    month?: string;
    fy?: number;
    hospitalId?: string | null;
  }) =>
    api
      .get<ApiResponse<HospitalPnlApiResponse>>("/api/hospital-admin/reports/pnl", {
        params: {
          mode: params.mode,
          ...(params.mode === "MTD" && params.month ? { month: params.month } : {}),
          ...((params.mode === "YTD" || params.mode === "MOM") && params.fy != null
            ? { fy: params.fy }
            : {}),
          ...(params.hospitalId != null && params.hospitalId ? { hospitalId: params.hospitalId } : {}),
        },
        timeout: params.mode === "MOM" ? 120_000 : 60_000,
      })
      .then((r) => r.data.data),

  pnlExpenseBreakdown: (params: {
    lineId: string;
    periodMode: 'MTD' | 'YTD';
    month?: string;
    fy?: number;
    hospitalId?: string | null;
  }) =>
    api
      .get<ApiResponse<PnlExpenseBreakdownResponse>>(
        '/api/hospital-admin/reports/pnl/expense-breakdown',
        {
          params: {
            lineId: params.lineId,
            periodMode: params.periodMode,
            ...(params.periodMode === 'MTD' && params.month ? { month: params.month } : {}),
            ...(params.periodMode === 'YTD' && params.fy != null ? { fy: params.fy } : {}),
            ...(params.hospitalId != null && params.hospitalId
              ? { hospitalId: params.hospitalId }
              : {}),
          },
        }
      )
      .then((r) => r.data.data),

  pnlAestheticProceduresBreakdown: (params: {
    periodMode: 'MTD' | 'YTD';
    month?: string;
    fy?: number;
    hospitalId?: string | null;
  }) =>
    api
      .get<ApiResponse<PnlAestheticProceduresBreakdownResponse>>(
        '/api/hospital-admin/reports/pnl/aesthetic-procedures-breakdown',
        {
          params: {
            periodMode: params.periodMode,
            ...(params.periodMode === 'MTD' && params.month ? { month: params.month } : {}),
            ...(params.periodMode === 'YTD' && params.fy != null ? { fy: params.fy } : {}),
            ...(params.hospitalId != null && params.hospitalId
              ? { hospitalId: params.hospitalId }
              : {}),
          },
        }
      )
      .then((r) => r.data.data),

  pnlIpdDirectCostBreakdown: (params: {
    lineId: string;
    periodMode: 'MTD' | 'YTD';
    month?: string;
    fy?: number;
    hospitalId?: string | null;
  }) =>
    api
      .get<ApiResponse<PnlIpdDirectCostBreakdownResponse>>(
        '/api/hospital-admin/reports/pnl/ipd-direct-cost-breakdown',
        {
          params: {
            lineId: params.lineId,
            periodMode: params.periodMode,
            ...(params.periodMode === 'MTD' && params.month ? { month: params.month } : {}),
            ...(params.periodMode === 'YTD' && params.fy != null ? { fy: params.fy } : {}),
            ...(params.hospitalId != null && params.hospitalId
              ? { hospitalId: params.hospitalId }
              : {}),
          },
        }
      )
      .then((r) => r.data.data),

  setCentralExpenseReportInclusion: (id: string, included: boolean) =>
    api
      .patch<ApiResponse<{ id: string; includedInReports: boolean }>>(
        `/api/hospital-admin/reports/central-expense/${id}/report-inclusion`,
        { included }
      )
      .then((r) => r.data.data),

  cashFlowBreakdown: (params: {
    lineId: string;
    periodMode: 'MTD' | 'YTD';
    month?: string;
    fy?: number;
    hospitalId?: string | null;
  }) =>
    api
      .get<ApiResponse<CashFlowBreakdownResponse>>(
        '/api/hospital-admin/reports/cash-flow/breakdown',
        {
          params: {
            lineId: params.lineId,
            periodMode: params.periodMode,
            ...(params.periodMode === 'MTD' && params.month ? { month: params.month } : {}),
            ...(params.periodMode === 'YTD' && params.fy != null ? { fy: params.fy } : {}),
            ...(params.hospitalId != null && params.hospitalId
              ? { hospitalId: params.hospitalId }
              : {}),
          },
        }
      )
      .then((r) => r.data.data),

  cashFlowBreakdownExport: (params: {
    lineId: string;
    periodMode: 'MTD' | 'YTD';
    month?: string;
    fy?: number;
    hospitalId?: string | null;
  }) =>
    api.get<Blob>('/api/hospital-admin/reports/cash-flow/breakdown/export', {
      params: {
        lineId: params.lineId,
        periodMode: params.periodMode,
        ...(params.periodMode === 'MTD' && params.month ? { month: params.month } : {}),
        ...(params.periodMode === 'YTD' && params.fy != null ? { fy: params.fy } : {}),
        ...(params.hospitalId != null && params.hospitalId
          ? { hospitalId: params.hospitalId }
          : {}),
      },
      responseType: 'blob',
    }),

  hospitalPnlExport: (params: {
    mode: "MTD" | "YTD" | "MOM";
    month?: string;
    fy?: number;
    hospitalId?: string | null;
  }) =>
    api.get<Blob>("/api/hospital-admin/reports/pnl/export", {
      params: {
        mode: params.mode,
        ...(params.mode === "MTD" && params.month ? { month: params.month } : {}),
        ...((params.mode === "YTD" || params.mode === "MOM") && params.fy != null
          ? { fy: params.fy }
          : {}),
        ...(params.hospitalId != null && params.hospitalId ? { hospitalId: params.hospitalId } : {}),
      },
      responseType: "blob",
    }),

  hospitalPnlOverrideUpsert: (params: {
    lineId: string;
    value: number;
    periodKey: string;
    periodMode?: "MTD" | "YTD";
    hospitalId?: string | null;
  }) =>
    api
      .put<ApiResponse<unknown>>("/api/hospital-admin/reports/pnl/overrides", {
        lineId: params.lineId,
        value: params.value,
        periodKey: params.periodKey,
        periodMode: params.periodMode ?? "MTD",
        ...(params.hospitalId != null && params.hospitalId
          ? { hospitalId: params.hospitalId }
          : {}),
      })
      .then((r) => r.data.data),

  hospitalPnlOverrideDelete: (params: {
    lineId: string;
    periodKey: string;
    periodMode?: "MTD" | "YTD";
    hospitalId?: string | null;
  }) =>
    api
      .delete<ApiResponse<unknown>>("/api/hospital-admin/reports/pnl/overrides", {
        params: {
          lineId: params.lineId,
          periodKey: params.periodKey,
          periodMode: params.periodMode ?? "MTD",
          ...(params.hospitalId != null && params.hospitalId
            ? { hospitalId: params.hospitalId }
            : {}),
        },
      })
      .then((r) => r.data),

  hospitalCashFlow: (params: {
    mode: "MTD" | "YTD" | "MOM";
    month?: string;
    fy?: number;
    hospitalId?: string | null;
  }) =>
    api
      .get<ApiResponse<HospitalCashFlowApiResponse>>("/api/hospital-admin/reports/cash-flow", {
        params: {
          mode: params.mode,
          ...(params.mode === "MTD" && params.month ? { month: params.month } : {}),
          ...((params.mode === "YTD" || params.mode === "MOM") && params.fy != null
            ? { fy: params.fy }
            : {}),
          ...(params.hospitalId != null && params.hospitalId
            ? { hospitalId: params.hospitalId }
            : {}),
        },
        timeout: params.mode === "MOM" ? 120_000 : 60_000,
      })
      .then((r) => r.data.data),

  hospitalCashFlowExport: (params: {
    mode: "MTD" | "YTD" | "MOM";
    month?: string;
    fy?: number;
    hospitalId?: string | null;
  }) =>
    api.get<Blob>("/api/hospital-admin/reports/cash-flow/export", {
      params: {
        mode: params.mode,
        ...(params.mode === "MTD" && params.month ? { month: params.month } : {}),
        ...((params.mode === "YTD" || params.mode === "MOM") && params.fy != null
          ? { fy: params.fy }
          : {}),
        ...(params.hospitalId != null && params.hospitalId
          ? { hospitalId: params.hospitalId }
          : {}),
      },
      responseType: "blob",
    }),

  hospitalCashFlowOverrideUpsert: (params: {
    lineId: string;
    value: number;
    periodKey: string;
    periodMode?: "MTD" | "YTD";
    hospitalId?: string | null;
  }) =>
    api
      .put<ApiResponse<unknown>>(
        "/api/hospital-admin/reports/cash-flow/overrides",
        {
          lineId: params.lineId,
          value: params.value,
          periodKey: params.periodKey,
          periodMode: params.periodMode ?? "MTD",
        },
        {
          params:
            params.hospitalId != null && params.hospitalId
              ? { hospitalId: params.hospitalId }
              : {},
        }
      )
      .then((r) => r.data.data),

  hospitalCashFlowOverrideDelete: (params: {
    lineId: string;
    periodKey: string;
    periodMode?: "MTD" | "YTD";
    hospitalId?: string | null;
  }) =>
    api
      .delete<ApiResponse<unknown>>("/api/hospital-admin/reports/cash-flow/overrides", {
        params: {
          lineId: params.lineId,
          periodKey: params.periodKey,
          periodMode: params.periodMode ?? "MTD",
          ...(params.hospitalId != null && params.hospitalId
            ? { hospitalId: params.hospitalId }
            : {}),
        },
      })
      .then((r) => r.data),
};

